/**
 * S05 · Executive Dashboard — CEO / Board Variant
 *
 * Wave 29 — Bundle-independent. Consumes the aggregate `/api/dashboard`
 * endpoint via `DashboardDataProvider`; each panel reads
 * `useDashboardData()` directly. Adds skeleton + error gates + the live
 * SyncedIndicator and a SystemStatusPanel at the foot of the page.
 */
import React from "react";
import {
  TrendingUp,
  Coins,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { DashboardDataProvider, useDashboardData } from "@/contexts/DashboardDataContext";
import { Flywheel } from "@/components/brand/Flywheel";
import { SyncedIndicator } from "@/components/data/SyncedIndicator";
import {
  fmtAud,
  KpiTile,
  SectionEyebrow,
} from "./components/DashboardPrimitives";
import { SystemStatusPanel } from "./components/SystemStatusPanel";
import HomeHero from "./components/HomeHero";
import { useTimeTravel } from "@/contexts/TimeTravelContext";
import {
  SpendValueChart,
  CeoDepartmentsPanel,
  VendorConcentrationPanel,
  CeoGovernancePanel,
  SignedReportsPanel,
  EstatePanel,
} from "./components/CeoPanels";
import { TopBurnersPanel } from "./components/CaioPanels";

export default function CeoDashboard() {
  return (
    <DashboardDataProvider>
      <CeoDashboardInner />
    </DashboardDataProvider>
  );
}

function CeoDashboardInner() {
  const {
    tenant,
    kpis,
    frameworks,
    trend12m,
    loading,
    error,
    lastSyncedAt,
    refetch,
  } = useDashboardData();
  const { viewAsOf, multiplier, label } = useTimeTravel();

  if (loading && !tenant) return <CeoSkeleton />;
  if (error) return <CeoError message={error.message} />;

  const ytdSpend = (trend12m || []).reduce((s, m) => s + (m.spendAud || 0), 0) * multiplier;
  const ytdValue = (trend12m || []).reduce((s, m) => s + (m.valueAud || 0), 0) * multiplier;
  const netValue = ytdValue - ytdSpend;
  const tGoverned = Math.round((kpis.governedAgents ?? 0) * multiplier);
  const tCoverage = Math.max(0, Math.round((kpis.avgComplianceCoverage ?? 0) * (0.65 + 0.35 * multiplier)));
  const showRewind = viewAsOf !== 0;

  return (
    <div
      className="relative px-6 lg:px-8 py-6 anim-fade-up"
      data-testid="ceo-dashboard"
    >
      <div className="absolute -right-24 -top-12 pointer-events-none">
        <Flywheel size={300} opacity={0.04} />
      </div>

      {/* Header strip — ceremonial */}
      <div className="relative">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="eyebrow">Boardroom · CEO view</p>
            <h1 className="text-display-md text-ink-primary mt-1">
              {tenant?.name}
            </h1>
            <p className="font-ceremonial italic text-ink-secondary text-[17px] mt-1 max-w-2xl">
              See your AI clearly. The estate is governed, the value is accruing,
              and every signed artefact is verifiable.
            </p>
            <p className="text-meta text-ink-disabled mt-2">
              {tenant?.industry} · {tenant?.region}
            </p>
          </div>
          <SyncedIndicator
            lastSyncedAt={lastSyncedAt}
            loading={loading}
            onRefetch={refetch}
            testId="ceo-dashboard-synced"
          />
        </div>
      </div>

      {/* Persona-aware hero + today's digest + live savings ticker */}
      <HomeHero />

      {showRewind && (
        <div
          className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-gold-600 bg-gold-300/15 text-gold-700 text-meta font-semibold"
          data-testid="ceo-time-rewind-banner"
        >
          <span className="font-bold tracking-[0.06em] uppercase text-[10px]">Rewind</span>
          Showing estate as of <span className="font-mono">{label}</span>
          <span className="text-ink-disabled">·</span>
          KPIs scaled by ×{multiplier.toFixed(2)}
        </div>
      )}

      {/* Hero KPI strip — board-grade */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <KpiTile
          label="AI value YTD"
          value={fmtAud(ytdValue)}
          delta="+38% YoY"
          deltaTone="up"
          hint="net of spend"
          icon={TrendingUp}
          tone="low"
          testId="ceo-kpi-value"
        />
        <KpiTile
          label="AI spend YTD"
          value={fmtAud(ytdSpend)}
          delta="−6% vs plan"
          deltaTone="up"
          hint={`net A$ ${(netValue / 1_000_000).toFixed(1)}M`}
          icon={Coins}
          tone="tralenex"
          testId="ceo-kpi-spend"
        />
        <KpiTile
          label="Governance coverage"
          value={`${tCoverage}%`}
          delta="+4 pts QoQ"
          deltaTone="up"
          hint={`${(frameworks || []).length} frameworks mapped`}
          icon={ShieldCheck}
          tone="gold"
          testId="ceo-kpi-governance"
        />
        <KpiTile
          label="Estate momentum"
          value={`${tGoverned}`}
          delta={`+${Math.round(tGoverned * 0.18)} this quarter`}
          deltaTone="up"
          hint="governed AI agents in production"
          icon={Sparkles}
          tone="opp"
          testId="ceo-kpi-momentum"
        />
      </div>

      {/* Body */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 flex flex-col gap-4">
          <SpendValueChart />
          <TopBurnersPanel />
          <CeoDepartmentsPanel />
          <VendorConcentrationPanel />
        </div>
        <div className="flex flex-col gap-4">
          <CeoGovernancePanel />
          <SignedReportsPanel />
          <EstatePanel />
        </div>
      </div>

      {/* Narrative strip */}
      <div
        className="mt-6 surface-card shadow-e1 p-5 flex items-start gap-3 border-l-[3px] border-l-gold-600"
        data-testid="ceo-narrative"
      >
        <Sparkles size={16} className="text-gold-600 mt-0.5 shrink-0" />
        <div>
          <SectionEyebrow>Boardroom narrative</SectionEyebrow>
          <p className="text-[15px] text-ink-primary leading-relaxed max-w-4xl">
            Our governed AI estate delivered{" "}
            <span className="font-semibold tabular-nums">{fmtAud(ytdValue)}</span> of measured
            value year-to-date against{" "}
            <span className="font-semibold tabular-nums">{fmtAud(ytdSpend)}</span> of AI spend —
            a ratio of{" "}
            <span className="font-semibold tabular-nums">{(kpis.roiRatio ?? 0).toFixed(1)}×</span>.
            Compliance coverage averages{" "}
            <span className="font-semibold tabular-nums">{kpis.avgComplianceCoverage ?? 0}%</span>{" "}
            across {(frameworks || []).length} regulatory frameworks. Every artefact in the board pack
            is signed and verifiable.
          </p>
        </div>
      </div>

      <div className="mt-4">
        <SystemStatusPanel />
      </div>
    </div>
  );
}

function CeoSkeleton() {
  return (
    <div className="px-6 lg:px-8 py-6 anim-fade-up" data-testid="ceo-dashboard-loading">
      <div className="h-8 w-72 rounded-md bg-inset/60 animate-pulse" />
      <div className="mt-2 h-4 w-96 rounded-md bg-inset/40 animate-pulse" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-md surface-card animate-pulse bg-inset/30" />
        ))}
      </div>
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 h-[480px] rounded-md surface-card animate-pulse bg-inset/20" />
        <div className="h-[480px] rounded-md surface-card animate-pulse bg-inset/20" />
      </div>
    </div>
  );
}

function CeoError({ message }) {
  return (
    <div className="px-6 lg:px-8 py-6" data-testid="ceo-dashboard-error">
      <div className="surface-card shadow-e1 p-5 border-l-[3px] border-l-risk-high">
        <p className="eyebrow text-risk-high">Boardroom · CEO view</p>
        <h1 className="text-h2 text-ink-primary mt-1">Couldn&apos;t load the dashboard</h1>
        <p className="mt-2 text-meta text-ink-secondary">{message || "Network error — please retry."}</p>
      </div>
    </div>
  );
}

